import { Button, Input } from "reactstrap";
import DynamicParam from "../../../components/Workflow/DynamicParam";
import DynamicSelectFunction from "../../../components/Workflow/DynamicSelectFunction";
import { MdDelete } from "react-icons/md";
import MonacoEditor from "@monaco-editor/react";
import FConstants from "../../../constants/FuncoesContants";
import { useEffect, useState } from "react";

const renderConditionalInput = (data, id_node, onParamChange, textOnly) => (
  <Input
    type={textOnly ? "text" : "select"}
    value={!textOnly ? data?.value : undefined}
    required={data.obrigatory}
    onChange={({ target }) => onParamChange(id_node, data.id, target.value)}
  >
    <option value="">Campo Vazio</option>
    <option value="==">Igual (==)</option>
    <option value="!=">Diferente (!=)</option>
    <option value=">">Maior {"(>)"}</option>
    <option value="<">Menor {"(<)"}</option>
    <option value="<=">Menor ou igual {"(<=)"}</option>
    <option value=">=">Maior ou igual {"(>=)"}</option>
    <option value="contains">Contém 🔎</option>
    <option value="not_contains">Não Contém ❌</option>
  </Input>
);

const renderSelectInput = (
  data,
  id_node,
  onParamChange,
  options = [],
  textOnly
) => (
  <Input
    type={textOnly ? "text" : "select"}
    value={!textOnly ? data?.value : undefined}
    required={data.obrigatory}
    onChange={({ target }) => onParamChange(id_node, data.id, target.value)}
  >
    <option value="">Campo Vazio</option>
    {options.map((group, groupIndex) => {
      const items = Object.values(group).filter(
        (item) => typeof item === "object" && item.type === "output"
      );

      if (items.length === 0) return null;

      return (
        <optgroup
          key={groupIndex}
          label={group.node_title || "Categoria"}
          style={{ fontStyle: "italic" }}
        >
          {items
            .filter((f) => f.variable_type === data.variable_type)
            .map((item) => (
              <option
                key={item.id}
                value={item.id}
                style={{ fontStyle: "italic" }}
              >
                {item.label}
              </option>
            ))}
        </optgroup>
      );
    })}
  </Input>
);

export default function RenderParamInput({
  data,
  id_node,
  onParamChange,
  options,
  textOnly = false,
  dynamicInput = [[], (_) => {}],
  onRefreshParams = (id) => {},
  node_type = "",
  visibleEditMode,
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(visibleEditMode);
  }, [visibleEditMode]);

  const renderInputByType = () => {
    switch (data.dataType) {
      case "condicional":
        return renderConditionalInput(data, id_node, onParamChange, textOnly);

      case "select":
        return renderSelectInput(
          data,
          id_node,
          onParamChange,
          options,
          textOnly
        );

      case "textbox":
        return (
          <Input
            type="textarea"
            required={data.obrigatory}
            value={!textOnly ? data?.value : undefined}
            className="textarea-input-lateral"
            style={{ width: "100%" }}
            onChange={({ target }) =>
              onParamChange(id_node, data.id, target.value)
            }
          />
        );

      case "number":
        return (
          <Input
            type="number"
            value={data?.value ?? undefined}
            onChange={({ target }) =>
              onParamChange(id_node, data.id, target.value)
            }
          />
        );

      case "checkbox":
        return (
          <Input
            type="checkbox"
            value={!textOnly ? data?.value : undefined}
            onChange={({ target }) =>
              onParamChange(id_node, data.id, target.value)
            }
          />
        );

      case "code":
        return (
          <div
            style={{
              border: "1px solid #ced4da",
              borderRadius: 2,
              padding: 2,
            }}
          >
            <MonacoEditor
              height={`200px`}
              language="python"
              theme="light"
              value={data?.value}
              onChange={(value) => onParamChange(id_node, data.id, value)}
            />
          </div>
        );

      case "arquivo":
      case "file":
        return !data?.value ? (
          <Input
            type="file"
            onChange={({ target }) => {
              const file = target.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () =>
                  onParamChange(id_node, data.id, reader.result);
                reader.readAsDataURL(file);
              }
            }}
          />
        ) : (
          <div
            style={{ display: "flex", gap: 5, justifyContent: "space-between" }}
          >
            <Input type="text" value={data.value} disabled />
            <Button
              size="sm"
              color="danger"
              onClick={() => onParamChange(id_node, data.id, null)}
            >
              <MdDelete />
            </Button>
          </div>
        );

      case "dynamic":
        return (
          <DynamicParam
            value={data?.value}
            id_node={id_node}
            onChange={(value) => onParamChange(id_node, data.id, value)}
            dynamicInput={dynamicInput}
          />
        );

      case "dynamic-simples":
        return (
          <DynamicParam
            value={data?.value}
            id_node={id_node}
            onChange={(value) => onParamChange(id_node, data.id, value)}
            dynamicInput={dynamicInput}
            urlHide={true}
            title="Entradas Dinâmicas:"
            subtitle=""
            onRefreshParams={onRefreshParams}
            node_type={node_type}
          />
        );

      case "function-search":
        return (
          <DynamicSelectFunction
            data={data}
            id_node={id_node}
            onParamChange={onParamChange}
          />
        );

      default:
        return (
          <Input
            type={data.dataType || "text"}
            value={!textOnly ? data?.value : undefined}
            required={data.obrigatory}
            onChange={({ target }) =>
              onParamChange(id_node, data.id, target.value)
            }
          />
        );
    }
  };

  return (
    <div className={`parametro-item mb-${!visible ? "2" : "4"}`} key={data.id}>
      {data.label && !data.label.startsWith("_") && (
        <label className="parametro-label mt-1">{data.label}:</label>
      )}
      {renderInputByType()}
      {visible && (
        <div className="mt-1">
          <label className="parametro-label mt-1" style={{ fontWeight: "500" }}>
            Tipo do Parâmetro:
          </label>

          <Input
            type="select"
            value={data?.dataType}
            onChange={({ target }) =>
              onParamChange(id_node, data.id, data.value, target.value)
            }
          >
            <option disabled selected />
            {FConstants.TIPOS_DE_CAMPOS_PARAMETROS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </Input>
        </div>
      )}
    </div>
  );
}
